import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type PropsWithChildren } from 'react';

import { queryClient } from '@core/api/queryClient';
import { buildClerkAppearance, ClerkTokenBridge, SessionSync } from '@core/auth';
import type { RuntimeEnv } from '@core/config/env';
import { ThemeProvider, useTheme } from '@core/hooks/useTheme';
import { I18nProvider } from '@core/i18n';
import { TooltipProvider } from '@core/ui';

interface AppProvidersProps extends PropsWithChildren {
  env: RuntimeEnv;
}

/**
 * The provider stack, ordered by what actually depends on what:
 *
 *   ClerkProvider          identity; must wrap anything calling useAuth
 *   └ ClerkTokenBridge     installs getToken for the axios interceptors
 *     └ QueryClientProvider
 *       └ SessionSync      clears caches when the signed-in user changes
 *
 * `LiveTrackingProvider` is deliberately NOT here. It opens an SSE stream, which needs
 * both a session and an organization, so it mounts inside the authenticated shell --
 * at the root it would open a stream on the sign-in page.
 */
export function AppProviders({ env, children }: AppProvidersProps) {
  // ThemeProvider wraps rather than nests here, because this component is itself a theme
  // consumer -- it feeds `resolved` into Clerk's appearance.
  return (
    <ThemeProvider>
      <ThemedProviders env={env}>{children}</ThemedProviders>
    </ThemeProvider>
  );
}

function ThemedProviders({ env, children }: AppProvidersProps) {
  const { resolved } = useTheme();
  const appearance = useMemo(() => buildClerkAppearance(resolved), [resolved]);

  return (
    <ClerkProvider
      publishableKey={env.CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/sign-in"
      appearance={appearance}
    >
      <ClerkTokenBridge>
        <QueryClientProvider client={queryClient}>
          <SessionSync />
          <I18nProvider>
            <TooltipProvider>{children}</TooltipProvider>
          </I18nProvider>
        </QueryClientProvider>
      </ClerkTokenBridge>
    </ClerkProvider>
  );
}
